import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  IconButton,
  Divider,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import CloseIcon from '@mui/icons-material/Close';
import { Button } from '@/components/ui';
import { merchantsService } from '../../../../services/merchantsService';
import type { Merchant } from '../../../../services/merchantsService';

const drawerWidth = 420;

// Interface for form data
interface GenerateSettlementFormData {
  merchant_uuid: string;
}

// Props interface
interface GenerateSettlementDrawerProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: GenerateSettlementFormData) => void;
}

export const GenerateSettlementDrawer: React.FC<GenerateSettlementDrawerProps> = ({
  open,
  onClose,
  onSubmit,
}) => {
  const [merchants, setMerchants] = useState<Merchant[]>([]);
  const [selectedMerchant, setSelectedMerchant] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Fetch merchants when drawer opens
  useEffect(() => {
    if (open) {
      fetchMerchants();
    }
  }, [open]);

  // Reset form when drawer closes
  useEffect(() => {
    if (!open) {
      setSelectedMerchant('');
      setError(null);
    }
  }, [open]);

  const fetchMerchants = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await merchantsService.getMerchants();
      setMerchants(response.results || []);
    } catch (err) {
      setError('Failed to fetch merchants. Please try again.');
      console.error('Error fetching merchants:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMerchantChange = (event: SelectChangeEvent<string>) => {
    setSelectedMerchant(event.target.value);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!selectedMerchant) {
      setError('Please select a merchant');
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit({ merchant_uuid: selectedMerchant });
    } catch (err) {
      // Error handling is done in the parent component
      console.error('Error generating settlement:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    setSelectedMerchant('');
    setError(null);
    onClose();
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: drawerWidth } }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" px={2} py={2}>
        <Typography variant="h6">Generate Settlement</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
      <Box component="form" onSubmit={(e) => e.preventDefault()} px={2} py={2} display="flex" flexDirection="column" gap={2}>
        <Typography variant="subtitle1" fontWeight="medium">
          Settlement Details
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Select a merchant to generate settlement report
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <FormControl fullWidth>
          <InputLabel id="merchant-select-label">Merchant</InputLabel>
          <Select
            labelId="merchant-select-label"
            id="merchant-select"
            value={selectedMerchant}
            label="Merchant"
            onChange={handleMerchantChange}
            disabled={loading}
          >
            {loading ? (
              <MenuItem disabled>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2">Loading merchants...</Typography>
                </Box>
              </MenuItem>
            ) : (
              merchants.map((merchant) => (
                <MenuItem key={merchant.uuid} value={merchant.uuid}>
                  <Box>
                    <Typography variant="body1" fontWeight="medium">
                      {merchant.name}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {merchant.email}
                    </Typography>
                  </Box>
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>

        <Box display="flex" gap={1} mt={1}>
          <Button
            variant="outlined"
            onClick={handleCancel}
            disabled={submitting}
            sx={{ flex: 1 }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={!selectedMerchant || submitting}
            loading={submitting}
            loadingText="Generating..."
            sx={{ flex: 1 }}
          >
            Generate Settlement
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default GenerateSettlementDrawer;
